import { useForm } from 'react-hook-form'
import { Button, Dropdown, StoreSelect, TextInput } from 'components/common'
import Modal from 'components/modal'
import toast from 'react-hot-toast'
import {
  createPurchaseTimer,
  updatePurchaseTimer,
} from 'utils/api/purchaseTimerApi'
import { PURCHASE_TIMER_FORM_FIELDS } from './utils'

const {
  BARCODE,
  STORE,
  AVAILABLE_TO_PURCHASE_TIMER,
  PURCHASE_START_TIME,
  PURCHASE_END_TIME,
} = PURCHASE_TIMER_FORM_FIELDS

export type PurchaseTimer = {
  _id: string
  barcode: string
  store: string
  availableToPurchaseTimer: boolean
  purchaseStartTime: string
  purchaseEndTime: string
  isImported?: boolean
  updatedAt?: string
  createdAt?: string
}

type Props = {
  isOpen: boolean
  closeModal: () => void
  barcode: string
  timer?: PurchaseTimer | null
  onSuccess: () => void
}

const PurchaseTimerFormModal = ({
  isOpen,
  closeModal,
  barcode,
  timer,
  onSuccess,
}: Props) => {
  const isEdit = Boolean(timer)

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    defaultValues: {
      barcode,
      store: timer?.store ?? '',
      availableToPurchaseTimer: timer?.availableToPurchaseTimer ?? true,
      purchaseStartTime: timer?.purchaseStartTime ?? '',
      purchaseEndTime: timer?.purchaseEndTime ?? '',
    },
  })

  const onSubmit = async (data: any) => {
    // Mirror backend validation client-side so we don't round-trip obvious errors.
    if (!isEdit && !data.store) {
      setError(STORE.key, { message: 'Store is required' })
      return
    }
    if (!data.purchaseStartTime) {
      setError(PURCHASE_START_TIME.key, { message: 'Start time is required' })
      return
    }
    if (!data.purchaseEndTime) {
      setError(PURCHASE_END_TIME.key, { message: 'End time is required' })
      return
    }
    // "HH:MM" 24h strings compare lexicographically === chronologically.
    if (data.purchaseEndTime <= data.purchaseStartTime) {
      setError(PURCHASE_END_TIME.key, {
        message: 'End time must be after start time (no overnight windows)',
      })
      return
    }

    const toastId = toast.loading(
      isEdit ? 'Updating Purchase Timer...' : 'Creating Purchase Timer...',
    )
    try {
      if (isEdit && timer) {
        await updatePurchaseTimer(timer._id, {
          availableToPurchaseTimer: data.availableToPurchaseTimer,
          purchaseStartTime: data.purchaseStartTime,
          purchaseEndTime: data.purchaseEndTime,
        })
      } else {
        await createPurchaseTimer({
          barcode: data.barcode,
          store: data.store,
          availableToPurchaseTimer: data.availableToPurchaseTimer,
          purchaseStartTime: data.purchaseStartTime,
          purchaseEndTime: data.purchaseEndTime,
        })
      }
      toast.success(
        isEdit
          ? 'Purchase Timer updated successfully!'
          : 'Purchase Timer created successfully!',
        { id: toastId },
      )
      onSuccess()
      closeModal()
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.msg
      toast.error(
        msg || `Failed to ${isEdit ? 'update' : 'create'} Purchase Timer.`,
        { id: toastId },
      )
    }
  }

  return (
    <Modal
      width={560}
      title={isEdit ? 'Edit Purchase Timer' : 'Add Purchase Timer'}
      isOpen={isOpen}
      closeModal={closeModal}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          {/* Barcode — fixed to the current product */}
          <TextInput
            register={register}
            name={BARCODE.key}
            label={BARCODE.label}
            disabled
          />

          {/* Store — editable on add, immutable on edit */}
          {isEdit ? (
            <TextInput
              register={register}
              name={STORE.key}
              label={STORE.label}
              disabled
            />
          ) : (
            <StoreSelect
              name={STORE.key}
              label={STORE.label}
              setValue={setValue}
              required
              error={errors?.[STORE.key]?.message as string}
            />
          )}

          {/* Availability */}
          <Dropdown
            name={AVAILABLE_TO_PURCHASE_TIMER.key}
            label={AVAILABLE_TO_PURCHASE_TIMER.label}
            setValue={setValue}
            defaultValue={timer?.availableToPurchaseTimer ?? true}
            error={errors?.[AVAILABLE_TO_PURCHASE_TIMER.key]?.message as string}
            options={[
              { label: 'Yes', value: true, disabled: false },
              { label: 'No', value: false, disabled: false },
            ]}
          />

          <TextInput
            register={register}
            name={PURCHASE_START_TIME.key}
            label={PURCHASE_START_TIME.label}
            type="time"
            required
            hintText="Dubai time (GST), 24h format (e.g. 09:00)"
            error={errors?.[PURCHASE_START_TIME.key]?.message as string}
          />

          <TextInput
            register={register}
            name={PURCHASE_END_TIME.key}
            label={PURCHASE_END_TIME.label}
            type="time"
            required
            hintText="Dubai time (GST), must be after start time"
            error={errors?.[PURCHASE_END_TIME.key]?.message as string}
          />
        </div>

        <div className="mt-6 flex gap-4">
          <Button
            buttonText={isEdit ? 'Save' : 'Create'}
            className="w-full"
            loading={isSubmitting}
          />
          <Button
            buttonText="Cancel"
            variant="outline"
            type="button"
            className="w-full"
            onClick={closeModal}
          />
        </div>
      </form>
    </Modal>
  )
}

export default PurchaseTimerFormModal
